import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiCheck, FiFilter, FiRefreshCw } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";

const FilterModal = ({ isOpen, onClose, onApply }) => {
  const [filters, setFilters] = useState({
    distributor_id: "",
    outlet_id: "",
    status: "",
    method: "",
  });

  const [outlets, setOutlets] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [searchDistributor, setSearchDistributor] = useState("");
  const [searchOutlet, setSearchOutlet] = useState("");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isOpen) {
      fetchAllDistributors();
      fetchAllOutlets();
    }
  }, [isOpen]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchAllDistributors = async () => {
    try {
      const first = await axios.get(
        `${BASE_URL}/v1/distributor?page=1`,
        getAuthHeaders()
      );

      let all = [...(first.data?.data || [])];
      const totalPages = first.data?.meta?.last_page || 1;

      for (let p = 2; p <= totalPages; p++) {
        const r = await axios.get(
          `${BASE_URL}/v1/distributor?page=${p}`,
          getAuthHeaders()
        );
        all = [...all, ...(r.data?.data || [])];
      }

      setDistributors(all);
    } catch (err) {
      console.error("Error fetching distributors:", err);
    }
  };

  const fetchAllOutlets = async () => {
    try {
      const first = await axios.get(
        `${BASE_URL}/v1/outlet?page=1`,
        getAuthHeaders()
      );

      let all = [...(first.data?.data || [])];
      const totalPages = first.data?.meta?.last_page || 1;

      for (let p = 2; p <= totalPages; p++) {
        const r = await axios.get(
          `${BASE_URL}/v1/outlet?page=${p}`,
          getAuthHeaders()
        );
        all = [...all, ...(r.data?.data || [])];
      }

      setOutlets(all);
    } catch (err) {
      console.error("Error fetching outlets:", err);
    }
  };

  if (!isOpen) return null;

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    const emptyFilters = {
      distributor_id: "",
      outlet_id: "",
      status: "",
      method: "",
    };
    setFilters(emptyFilters);
    setSearchDistributor("");
    setSearchOutlet("");
    onApply(emptyFilters);
  };

  const filteredDistributors = distributors.filter((d) =>
    (d.name || "").toLowerCase().includes(searchDistributor.toLowerCase())
  );

  const filteredOutlets = outlets.filter((o) =>
    (o.name || "").toLowerCase().includes(searchOutlet.toLowerCase())
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 flex flex-col"
        >
          {/* Header */}
          <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                <FiFilter className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Filter Purchasing Order</h2>
                <p className="text-xs text-gray-500">Filter berdasarkan distributor, outlet, dan status</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4 text-xs overflow-y-auto max-h-[65vh]">
            {/* Distributor Select */}
            <div className="space-y-1.5">
              <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                Distributor (PBF)
              </label>
              <select
                value={filters.distributor_id}
                onChange={(e) => setFilters({ ...filters, distributor_id: e.target.value })}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="">-- Semua Distributor --</option>
                {distributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.kota || "Pusat"})
                  </option>
                ))}
              </select>
            </div>

            {/* Outlet Select */}
            <div className="space-y-1.5">
              <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                Outlet Penempatan
              </label>
              <select
                value={filters.outlet_id}
                onChange={(e) => setFilters({ ...filters, outlet_id: e.target.value })}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="">-- Semua Outlet --</option>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Payment Method */}
              <div className="space-y-1.5">
                <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                  Metode Pembayaran
                </label>
                <select
                  value={filters.method}
                  onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  <option value="">Semua Metode</option>
                  <option value="cash">Cash (Tunai)</option>
                  <option value="credit">Credit (Kredit/Tempo)</option>
                </select>
              </div>

              {/* Status PO */}
              <div className="space-y-1.5">
                <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                  Status PO
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                >
                  <option value="">Semua Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="received">Received</option>
                  <option value="canceled">Canceled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
            <button
              onClick={handleReset}
              className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-semibold transition-colors"
              >
                Batal
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleApply}
                className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
              >
                <FiCheck className="w-4 h-4" />
                <span>Terapkan</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FilterModal;
